import React from 'react';
import PropTypes from 'prop-types';
import { useAsyncDebounce } from 'react-table';
import { useParams } from 'react-router-dom';
import { useTabContext } from 'GlobalState/Context/TabContext';
import { useGlobalContext } from 'GlobalState/Context/GlobalContext';

// Form
import { useForm } from 'react-hook-form';
import InputFieldNoLabel from '../InputFieldNoLabel';

// styles
import formStyles from '../Form.module.scss';

// Data
import { useMutation, useQueryClient } from 'react-query';
import instance from 'axiosInstance';

const submitMultiplier = async (variables) => {
  const params = new URLSearchParams();
  params.append('multiplier', variables.multiplier);
  params.append('employee_id', variables.id);
  params.append('project_slug', variables.slug);
  params.append('created_on', variables.billingPeriod);

  const response = await instance.post(
    '/private/admin/employee-multiplier',
    params
  );
  return response.data;
};

const MultiplierForm = ({ id, multiplier = 1 }) => {
  const queryClient = useQueryClient();
  const { globalDispatch } = useGlobalContext();

  const { slug } = useParams();
  const {
    state: { billingPeriod },
  } = useTabContext();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { multiplier } });

  const { mutate } = useMutation(submitMultiplier, {
    mutationKey: 'multiplier',
    onSuccess: () => {
      queryClient.invalidateQueries(['project_components', slug]);
      queryClient.invalidateQueries(['involved-members', slug]);
      globalDispatch({
        type: 'pushNotification',
        payload: { type: 'success', text: 'Multiplier updated!' },
      });
    },
    onError: () => {
      globalDispatch({
        type: 'pushNotification',
        payload: { type: 'error', text: 'Something went wrong :(' },
      });
    },
  });

  const debounceSubmit = useAsyncDebounce(() => {
    handleSubmit(onSubmit)();
  }, 400);

  const onSubmit = (data) => {
    data.id = id;
    data.slug = slug;
    data.billingPeriod = billingPeriod.value;

    if (data.multiplier !== multiplier) {
      mutate(data);
    }
  };

  const { name, onChange, onBlur, ref } = register('multiplier', {
    required: 'Required',
    valueAsNumber: true,
    min: {
      value: 0.1,
      message: 'Value must be between 0.1 and 2',
    },
    max: {
      value: 2,
      message: 'Value must be between 0.1 and 2',
    },
    pattern: {
      value: /^\d+(?:\.\d{1,2})?$/,
      message: "Value shouldn't have more than 2 numbers after decimal",
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <InputFieldNoLabel
        type="number"
        errors={errors.multiplier}
        wrapperClassName={formStyles['in-table-wrapper']}
        name={name}
        onBlur={onBlur}
        ref={ref}
        onChange={(e) => {
          onChange(e);
          debounceSubmit();
        }}
      />
    </form>
  );
};

MultiplierForm.propTypes = {
  id: PropTypes.number,
  multiplier: PropTypes.number,
};

export default MultiplierForm;
